/**
 * Screenplay & TV Writing Guide
 * Path to Netflix, studios, and streaming platforms
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Layout } from '../components/Layout';
import { SeoHead } from '../components/SeoHead';
import { SEO } from '../config/seo';
import { 
  Film, Check, X, AlertTriangle, ExternalLink, 
  TrendingUp, Target, Award, Zap, Mail, Shield
} from 'lucide-react';

export function ScreenplayGuide() {
  const { user } = useAuth();
  const [showConsultModal, setShowConsultModal] = useState(false);

  const pathways = [
    {
      title: "Feature Film Path",
      icon: <Film className="w-8 h-8 text-purple-600" />,
      difficulty: "Hard",
      timeline: "1-3 years typically",
      potential: "$100K-$500K+ if optioned/sold",
      steps: [
        "Write polished 90-120 page screenplay",
        "Register with WGA ($20) + Copyright Office ($65)",
        "Enter top-tier competitions (Nicholl, Austin, PAGE)",
        "Query managers and agents (after placing in contests)",
        "Get representation (essential for studio sales)",
        "Studio pitch meetings (through your rep)",
        "Option agreement ($5K-$50K) or outright sale"
      ]
    },
    {
      title: "TV Pilot Path",
      icon: <TrendingUp className="w-8 h-8 text-blue-600" />,
      difficulty: "Very Hard",
      timeline: "2-5 years to sale",
      potential: "$20K-$200K+ per episode if produced",
      steps: [
        "Write pilot (30 or 60 min) + series bible",
        "Register everything (WGA + Copyright)",
        "Build writing portfolio (multiple pilots)",
        "Get TV writing job (writers room) first",
        "Network with showrunners and producers",
        "Pitch through established channels only",
        "TV deals usually require proven track record"
      ]
    },
    {
      title: "Streaming/Netflix Path",
      icon: <Target className="w-8 h-8 text-red-600" />,
      difficulty: "Hard",
      timeline: "1-3 years",
      potential: "$50K-$300K if produced",
      steps: [
        "Write feature or limited series (6-8 episodes)",
        "Make it undeniably great (streaming is picky)",
        "Protect with WGA + Copyright immediately",
        "Target production companies that sell to streamers",
        "Manager/agent pitches to streaming buyers",
        "Alternative: Enter Netflix-backed competitions",
        "Package with talent if possible (director/actor)"
      ]
    }
  ];

  const competitions = [
    {
      name: "Nicholl Fellowship (Academy)",
      deadline: "May",
      prize: "$35K + meetings",
      prestige: "⭐⭐⭐⭐⭐",
      url: "https://www.oscars.org/nicholl",
      note: "Top 1% - career-making"
    },
    {
      name: "Austin Film Festival",
      deadline: "May",
      prize: "$5K + industry access",
      prestige: "⭐⭐⭐⭐⭐",
      url: "https://austinfilmfestival.com",
      note: "Best for networking"
    },
    {
      name: "PAGE Awards",
      deadline: "March",
      prize: "$25K",
      prestige: "⭐⭐⭐⭐",
      url: "https://pageawards.com",
      note: "Trusted by industry"
    },
    {
      name: "Sundance Screenwriters Lab",
      deadline: "September",
      prize: "Development support",
      prestige: "⭐⭐⭐⭐⭐",
      url: "https://www.sundance.org",
      note: "Elite development program"
    }
  ];

  const dosAndDonts = {
    dos: [
      "DO register with WGA AND Copyright Office before sharing anything",
      "DO write multiple scripts before querying (shows you are serious)",
      "DO enter top-tier competitions (Nicholl, Austin, Sundance, PAGE)",
      "DO get a manager BEFORE an agent (managers develop talent)",
      "DO move to LA if you are serious about TV writing",
      "DO network at film festivals and industry events",
      "DO follow proper screenplay formatting (use Final Draft or equivalent)",
      "DO keep scripts under 120 pages for features (110 is ideal)",
      "DO research production companies before pitching",
      "DO protect yourself with NDAs when possible"
    ],
    donts: [
      "DON'T cold-email your script to studios (it will be deleted)",
      "DON'T copyright every draft (only register final versions)",
      "DON'T send unsolicited scripts to Netflix/Hulu/Amazon (they return unread)",
      "DON'T skip WGA registration ($20) - it is industry standard",
      "DON'T pitch without a logline and one-pager ready",
      "DON'T write sequels to existing franchises (waste of time)",
      "DON'T expect to sell your first script (average is 5-10 scripts before sale)",
      "DON'T work with producers who ask YOU to pay them",
      "DON'T sign anything without a lawyer reviewing it",
      "DON'T post full scripts online (Script Revolution is risky)"
    ]
  };

  const redFlags = [
    {
      warning: "Producer wants money upfront",
      reality: "Real producers never charge writers. Run immediately.",
      icon: "🚩"
    },
    {
      warning: "Option agreement with no money",
      reality: "Free options are worthless. Demand at least $1,000-$5,000.",
      icon: "🚩"
    },
    {
      warning: "No production credits on IMDb",
      reality: "If they have not produced anything, they cannot produce yours.",
      icon: "🚩"
    },
    {
      warning: "Asking for multiple rewrites before option",
      reality: "Free development work. Option first, then discuss changes.",
      icon: "🚩"
    },
    {
      warning: "Shopping agreement without exclusivity timeline",
      reality: "Keeps your script hostage forever. Demand 6-12 month max.",
      icon: "🚩"
    }
  ];

  return (
    <Layout>
      <SeoHead {...SEO.screenplay} />
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-red-50 to-orange-50">
        <div className="container mx-auto px-4 py-12">
        {/* Hero Section */}
        <div className="text-center max-w-4xl mx-auto mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-100 border-2 border-purple-300 rounded-full mb-4">
            <Film className="w-5 h-5 text-purple-600" />
            <span className="font-semibold text-purple-900">Hollywood, Netflix, Streaming Platforms</span>
          </div>
          
          <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-purple-600 to-red-600">
            How to Sell Your Screenplay or TV Pilot
          </h1>
          
          <p className="text-xl text-gray-700 mb-6">
            The brutal truth about breaking into Hollywood, Netflix, and streaming platforms. 
            This is NOT like publishing a novel - there is no self-publishing equivalent.
          </p>

          <Alert className="bg-red-50 border-red-300">
            <AlertTriangle className="w-5 h-5 text-red-600" />
            <AlertDescription className="text-left">
              <strong className="text-red-900">Reality Check:</strong> Selling a screenplay is 10x harder than publishing a novel. 
              Average: <strong>5-10 scripts</strong> written before first sale. Timeline: <strong>2-5 years</strong> minimum. 
              But if you make it, payoffs can be <strong>$100K-$500K+</strong> per script.
            </AlertDescription>
          </Alert>
        </div>

        {/* Three Pathways */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold mb-2 text-center">
            Three Main Pathways
          </h2>
          <p className="text-center text-gray-600 mb-8">
            Choose your route based on your goals and patience level
          </p>

          <div className="grid md:grid-cols-3 gap-6">
            {pathways.map((pathway, idx) => (
              <Card key={idx} className="border-2 border-purple-200">
                <CardHeader>
                  <div className="flex justify-center mb-3">
                    {pathway.icon}
                  </div>
                  <CardTitle className="text-xl text-center">{pathway.title}</CardTitle>
                  <CardDescription className="text-center">
                    <div className="space-y-1 mt-3">
                      <div className="text-sm">
                        <strong>Difficulty:</strong> {pathway.difficulty}
                      </div>
                      <div className="text-sm">
                        <strong>Timeline:</strong> {pathway.timeline}
                      </div>
                      <div className="text-sm font-semibold text-green-700">
                        <strong>Potential:</strong> {pathway.potential}
                      </div>
                    </div>
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <ol className="space-y-2">
                    {pathway.steps.map((step, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm">
                        <span className="flex-shrink-0 w-6 h-6 bg-purple-100 text-purple-700 rounded-full flex items-center justify-center text-xs font-bold">
                          {i + 1}
                        </span>
                        <span className="text-gray-700 pt-0.5">{step}</span>
                      </li>
                    ))}
                  </ol>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Top Competitions */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold mb-2 text-center">
            Top Screenplay Competitions
          </h2>
          <p className="text-center text-gray-600 mb-8">
            Winning or placing opens doors - managers and agents read winners
          </p>

          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {competitions.map((comp, idx) => (
              <Card key={idx}>
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <CardTitle className="text-lg mb-1">{comp.name}</CardTitle>
                      <div className="text-sm text-gray-600">{comp.prestige}</div>
                    </div>
                    <a 
                      href={comp.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-purple-600 hover:text-purple-700"
                    >
                      <ExternalLink className="w-5 h-5" />
                    </a>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Deadline:</span>
                      <span className="font-semibold">{comp.deadline}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Prize:</span>
                      <span className="font-semibold text-green-700">{comp.prize}</span>
                    </div>
                    <div className="mt-3 p-2 bg-purple-50 rounded text-xs font-semibold text-purple-900">
                      {comp.note}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* DO's and DON'Ts */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold mb-2 text-center">
            Hollywood Rules - DO&rsquo;s and DON&rsquo;Ts
          </h2>
          <p className="text-center text-gray-600 mb-8">
            Follow these or waste years of your life
          </p>

          <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {/* DO's */}
            <Card className="border-2 border-green-300 bg-green-50">
              <CardHeader>
                <CardTitle className="text-2xl text-green-900 flex items-center gap-2">
                  <Check className="w-6 h-6" />
                  DO These
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  {dosAndDonts.dos.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm">
                      <Check className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* DON'Ts */}
            <Card className="border-2 border-red-300 bg-red-50">
              <CardHeader>
                <CardTitle className="text-2xl text-red-900 flex items-center gap-2">
                  <X className="w-6 h-6" />
                  DON&rsquo;T Do These
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  {dosAndDonts.donts.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm">
                      <X className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Red Flags */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold mb-2 text-center text-red-900">
            🚩 MASSIVE Red Flags - Run Immediately
          </h2>
          <p className="text-center text-gray-600 mb-8">
            If you see any of these, you are being scammed
          </p>

          <div className="max-w-4xl mx-auto space-y-4">
            {redFlags.map((flag, idx) => (
              <Alert key={idx} className="bg-red-50 border-2 border-red-400">
                <AlertTriangle className="w-5 h-5 text-red-600" />
                <AlertDescription>
                  <div className="flex items-start gap-3">
                    <div className="text-3xl">{flag.icon}</div>
                    <div className="flex-1">
                      <div className="font-bold text-red-900 mb-1">{flag.warning}</div>
                      <div className="text-sm text-gray-700">{flag.reality}</div>
                    </div>
                  </div>
                </AlertDescription>
              </Alert>
            ))}
          </div>
        </div>

        {/* Script Protection */}
        <div className="mb-12">
          <Card className="bg-gradient-to-r from-blue-600 to-purple-600 text-white border-0">
            <CardContent className="p-8">
              <div className="max-w-3xl mx-auto text-center">
                <Shield className="w-16 h-16 mx-auto mb-4" />
                <h3 className="text-3xl font-bold mb-4">
                  Protect Your Screenplay BEFORE Sharing
                </h3>
                <p className="text-blue-100 mb-6">
                  Script theft is REAL in Hollywood. Protect yourself with proper registration 
                  before you send to anyone. This is even more critical than novels.
                </p>
                
                <div className="grid md:grid-cols-2 gap-6 mb-6 text-left">
                  <div className="bg-white/10 backdrop-blur rounded-lg p-4">
                    <h4 className="font-bold text-lg mb-3">Required:</h4>
                    <ul className="space-y-2 text-sm text-blue-100">
                      <li className="flex items-center gap-2">
                        <Check className="w-4 h-4" />
                        <span>WGA Registry: $20 (industry standard)</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <Check className="w-4 h-4" />
                        <span>Copyright Office: $65 (legal protection)</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <Check className="w-4 h-4" />
                        <span>Do BOTH before any pitches</span>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-white/10 backdrop-blur rounded-lg p-4">
                    <h4 className="font-bold text-lg mb-3">Our Service:</h4>
                    <ul className="space-y-2 text-sm text-blue-100">
                      <li className="flex items-center gap-2">
                        <Check className="w-4 h-4" />
                        <span>We handle both registrations: $99</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <Check className="w-4 h-4" />
                        <span>Professional PDF prep included</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <Check className="w-4 h-4" />
                        <span>Submit everything correctly first time</span>
                      </li>
                    </ul>
                  </div>
                </div>

                <Link to="/copyright-protection">
                  <Button size="lg" className="bg-white text-purple-600 hover:bg-gray-100">
                    Protect Your Script Now ($99) →
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Reality Check */}
        <div className="mb-12">
          <Card className="bg-gray-900 text-white">
            <CardContent className="p-8">
              <h3 className="text-2xl font-bold mb-6 text-center">The Brutal Truth About Screenwriting</h3>
              
              <div className="grid md:grid-cols-3 gap-6 text-center">
                <div>
                  <div className="text-4xl font-bold text-red-400 mb-2">99.9%</div>
                  <div className="text-sm text-gray-300">of scripts never get produced</div>
                </div>
                <div>
                  <div className="text-4xl font-bold text-yellow-400 mb-2">5-10</div>
                  <div className="text-sm text-gray-300">scripts written before first sale (average)</div>
                </div>
                <div>
                  <div className="text-4xl font-bold text-green-400 mb-2">2-5 years</div>
                  <div className="text-sm text-gray-300">from first script to first sale (typical)</div>
                </div>
              </div>

              <div className="mt-8 p-4 bg-white/10 rounded-lg">
                <p className="text-sm text-gray-300 leading-relaxed">
                  💡 <strong>But here is the upside:</strong> If you DO break through, payoffs are massive. 
                  Feature sales: $100K-$500K+. TV staff writer: $3K-$6K per week. Showrunner: $30K+ per episode. 
                  The 0.1% who make it can earn millions. Just know what you are signing up for.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* CTA */}
        <div className="text-center">
          <Card className="max-w-3xl mx-auto border-2 border-purple-300">
            <CardContent className="p-8">
              <h3 className="text-2xl font-bold mb-4">
                Write Your Screenplay with UPost
              </h3>
              <p className="text-gray-700 mb-6">
                Professional screenplay formatting, AI writing assistance, collaboration tools, 
                and protection guidance - everything you need in one platform.
              </p>
              
              <div className="space-y-3 mb-6">
                <Link to="/register">
                  <Button className="w-full bg-purple-600 hover:bg-purple-700" size="lg">
                    Start Writing Your Script Free →
                  </Button>
                </Link>
                
                <div className="grid md:grid-cols-2 gap-3">
                  <Link to="/copyright-protection">
                    <Button variant="outline" className="w-full border-2">
                      Protect Your Script ($99)
                    </Button>
                  </Link>
                  <Link to="/pricing">
                    <Button variant="outline" className="w-full border-2">
                      See All Plans
                    </Button>
                  </Link>
                </div>
              </div>

              {user && (
                <Link to="/dashboard">
                  <Button variant="outline" className="w-full">
                    Go to Dashboard →
                  </Button>
                </Link>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
      </div>
    </Layout>
  );
}
